import {
  differenceInCalendarMonths,
  eachMonthOfInterval,
  format,
} from 'date-fns'

import type { Statement, Transaction } from '@/modules/projects'
import { countsAsMoneyFlow } from '@/modules/projects/utils'
import { parseCalendarDate } from '@/utils'
import type { StatementCoverage } from '../StatementCoverage/types'

// One month on the timeline: money in and money out, summed and made positive.
export interface ActivityMonth {
  key: string
  label: string
  shortLabel: string
  inflow: number
  outflow: number
}

export interface CaseActivity {
  months: ActivityMonth[]
  totalInflow: number
  totalOutflow: number
  net: number
}

export interface CaseSpan {
  start?: Date
  end?: Date
  months: number
}

// A mis-read year far in the past could ask for thousands of month columns; cap the timeline.
const MAX_MONTHS = 180

const monthKey = (date: Date): string => format(date, 'yyyy-MM')

// The case's time span. Prefer the statement coverage span (what the documents actually cover),
// then the first/last transaction date, then segment dates so a portfolio-only case still shows
// its real period instead of "No statements yet".
export const resolveCaseSpan = (
  coverage: StatementCoverage | null,
  transactions: Transaction[],
  statements: Statement[] = [],
): CaseSpan => {
  let start = coverage?.globalMin
  let end = coverage?.globalMax

  if (!start || !end) {
    for (const transaction of transactions) {
      if (!transaction.date) continue
      const date = parseCalendarDate(transaction.date)
      if (Number.isNaN(date.getTime())) continue
      if (!start || date < start) start = date
      if (!end || date > end) end = date
    }
  }

  if (!start || !end) {
    for (const statement of statements) {
      for (const segment of statement.segments ?? []) {
        for (const isoDate of [segment.startDate, segment.endDate]) {
          if (!isoDate) continue
          const date = parseCalendarDate(isoDate)
          if (Number.isNaN(date.getTime())) continue
          if (!start || date < start) start = date
          if (!end || date > end) end = date
        }
      }
    }
  }

  const months =
    start && end && start <= end
      ? differenceInCalendarMonths(end, start) + 1
      : 0
  return { start, end, months }
}

// Bucket signed amounts into per-month inflow/outflow plus case totals. Months with no activity
// still appear so the timeline reads as a continuous record, not a sparse one.
export const buildCaseActivity = (
  transactions: Transaction[],
  span: CaseSpan,
): CaseActivity => {
  let totalInflow = 0
  let totalOutflow = 0
  const inflowByMonth = new Map<string, number>()
  const outflowByMonth = new Map<string, number>()

  for (const transaction of transactions) {
    if (!transaction.date) continue
    if (!countsAsMoneyFlow(transaction)) continue
    const date = parseCalendarDate(transaction.date)
    if (Number.isNaN(date.getTime())) continue
    const key = monthKey(date)
    if (transaction.amount >= 0) {
      totalInflow += transaction.amount
      inflowByMonth.set(key, (inflowByMonth.get(key) ?? 0) + transaction.amount)
    } else {
      const magnitude = Math.abs(transaction.amount)
      totalOutflow += magnitude
      outflowByMonth.set(key, (outflowByMonth.get(key) ?? 0) + magnitude)
    }
  }

  let months: ActivityMonth[] = []
  if (span.start && span.end && span.start <= span.end) {
    let interval = eachMonthOfInterval({ start: span.start, end: span.end })
    if (interval.length > MAX_MONTHS) interval = interval.slice(-MAX_MONTHS)
    months = interval.map((date) => {
      const key = monthKey(date)
      return {
        key,
        label: format(date, 'MMM yyyy'),
        shortLabel: `${format(date, 'MMM')} '${format(date, 'yy')}`,
        inflow: inflowByMonth.get(key) ?? 0,
        outflow: outflowByMonth.get(key) ?? 0,
      }
    })
  }

  return { months, totalInflow, totalOutflow, net: totalInflow - totalOutflow }
}
